import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  MessageSquare,
  ChevronLeft,
  Send,
  Users,
  Briefcase,
  Loader2,
  Plus,
  Search,
  X
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import TenantMobileLayout from './TenantMobileLayout';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '../../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const TenantMobileChat = () => {
  const navigate = useNavigate();
  const { roomId } = useParams();
  const { user, api } = useAuth();
  const {
    rooms,
    activeRoom,
    messages,
    loading,
    canUseChat,
    openRoom,
    closeChat,
    sendMessage,
    fetchRooms,
    createRoom
  } = useChat();

  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef(null);

  // New room state
  const [showNewRoomSheet, setShowNewRoomSheet] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomType, setNewRoomType] = useState('general');
  const [operators, setOperators] = useState([]);
  const [selectedOperators, setSelectedOperators] = useState([]);
  const [creatingRoom, setCreatingRoom] = useState(false);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Open room if roomId provided
  useEffect(() => {
    if (roomId && rooms.length > 0) {
      const room = rooms.find(r => r.id === roomId);
      if (room && (!activeRoom || activeRoom.id !== roomId)) {
        openRoom(room);
      }
    }
  }, [roomId, rooms, activeRoom, openRoom]);

  // Fetch rooms on mount
  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  // Fetch operators for new room creation
  useEffect(() => {
    const fetchOperators = async () => {
      try {
        const response = await api.get('/operators');
        setOperators(response.data || []);
      } catch (err) {
        console.error('Failed to fetch operators:', err);
      }
    };
    fetchOperators();
  }, [api]);

  const handleSend = async () => {
    if (!messageInput.trim() || sending) return;

    setSending(true);
    try {
      await sendMessage(messageInput.trim());
      setMessageInput('');
    } catch (err) {
      console.error('Failed to send:', err);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleBack = () => {
    if (activeRoom) {
      closeChat();
      // Use replace to avoid adding to history stack
      navigate('/tenant-mobile/chat', { replace: true });
    } else {
      navigate('/tenant-mobile');
    }
  };

  const handleCreateRoom = async () => {
    if (!newRoomName.trim()) {
      toast.error('Please enter a room name');
      return;
    }

    setCreatingRoom(true);
    try {
      const room = await createRoom(newRoomName, newRoomType, null, selectedOperators);
      toast.success('Chat room created');
      setShowNewRoomSheet(false);
      setNewRoomName('');
      setSelectedOperators([]);
      if (room) {
        openRoom(room);
        navigate(`/tenant-mobile/chat/${room.id}`);
      }
    } catch (err) {
      toast.error('Failed to create room');
    } finally {
      setCreatingRoom(false);
    }
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return format(date, 'HH:mm');
    } else if (diffDays === 1) {
      return 'Yesterday ' + format(date, 'HH:mm');
    }
    return format(date, 'dd/MM HH:mm');
  };

  const toggleOperator = (operatorId) => {
    setSelectedOperators(prev => 
      prev.includes(operatorId) 
        ? prev.filter(id => id !== operatorId)
        : [...prev, operatorId]
    );
  };

  // Feature not available
  if (!canUseChat) {
    return (
      <TenantMobileLayout>
        <div className="bg-[#2a2a2a] px-4 py-4 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/tenant-mobile')} 
              className="text-gray-400 p-2"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-white font-bold text-lg">Messages</h1>
          </div>
        </div>
        <div className="p-4">
          <Card className="bg-[#2a2a2a] border-gray-700">
            <CardContent className="py-12 text-center">
              <MessageSquare className="h-12 w-12 mx-auto text-gray-600 mb-3" />
              <p className="text-white font-medium mb-2">Messaging Not Available</p>
              <p className="text-gray-400 text-sm">
                Upgrade your plan to access the messaging feature.
              </p>
            </CardContent>
          </Card>
        </div>
      </TenantMobileLayout>
    );
  }

  // Chat Room View (Active Conversation)
  if (activeRoom) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex flex-col">
        {/* Header */}
        <div className="bg-[#2a2a2a] px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
          <button onClick={handleBack} className="text-gray-400 p-1" data-testid="back-btn">
            <ChevronLeft className="h-6 w-6" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-white font-semibold truncate">{activeRoom.name}</h1>
            <p className="text-gray-400 text-xs">
              {activeRoom.participants?.length || 0} participants
            </p>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 pb-24">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-[#ffd108]" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No messages yet</p>
              <p className="text-sm">Start the conversation!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((msg, idx) => {
                const isOwn = msg.sender_id === user?.id;
                const isSystem = msg.type === 'system';

                if (isSystem) {
                  return (
                    <div key={msg.id || idx} className="text-center text-xs text-gray-500 py-1">
                      {msg.content}
                    </div>
                  );
                }

                return (
                  <div
                    key={msg.id || idx}
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg px-3 py-2 ${
                        isOwn
                          ? 'bg-[#ffd108] text-black'
                          : 'bg-[#2a2a2a] text-white'
                      }`}
                    >
                      {!isOwn && (
                        <p className="text-xs font-medium mb-1 text-[#ffd108]">
                          {msg.sender_name}
                        </p>
                      )}
                      <p className="text-sm whitespace-pre-wrap break-words">
                        {msg.content}
                      </p>
                      <p className={`text-[10px] mt-1 ${isOwn ? 'text-black/60' : 'text-gray-500'}`}>
                        {formatMessageTime(msg.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input - Fixed at bottom */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-[#2a2a2a] border-t border-gray-700">
          <div className="flex gap-2">
            <Input
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              disabled={sending}
              className="flex-1 bg-[#1a1a1a] border-gray-600 text-white placeholder:text-gray-500"
              data-testid="message-input"
            />
            <Button
              onClick={handleSend}
              disabled={!messageInput.trim() || sending}
              className="bg-[#ffd108] hover:bg-[#e5bc07] text-black"
              data-testid="send-btn"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Parse room name into title and site
  const parseRoomName = (name) => {
    if (!name) return { title: 'Unknown', site: null };
    const parts = name.split(' - ');
    if (parts.length >= 3) {
      return { 
        title: `${parts[0]} - ${parts[1]}`, 
        site: parts.slice(2).join(' - ') 
      };
    } else if (parts.length === 2) {
      return { title: parts[0], site: parts[1] };
    }
    return { title: name, site: null };
  };

  // Filter rooms by search
  const filteredRooms = rooms.filter(room => 
    !searchTerm || room.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Room List View
  return (
    <TenantMobileLayout>
      {/* Header */}
      <div className="bg-[#2a2a2a] px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/tenant-mobile')} 
              className="text-gray-400 p-2"
              data-testid="back-btn"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-[#ffd108]" />
              <h1 className="text-white font-bold text-lg">Messages</h1>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowNewRoomSheet(true)}
            className="text-[#ffd108] p-2"
            data-testid="new-chat-btn"
          >
            <Plus className="h-5 w-5" />
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 bg-[#1a1a1a] border-gray-700 text-white placeholder:text-gray-500"
            data-testid="search-input"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Room List */}
      <div className="p-4 pb-24">
        {filteredRooms.length === 0 ? (
          <Card className="bg-[#2a2a2a] border-gray-700">
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 mx-auto text-gray-600 mb-3" />
              <p className="text-white font-medium">
                {searchTerm ? 'No matching conversations' : 'No conversations yet'}
              </p>
              <p className="text-gray-400 text-sm mt-1">
                {searchTerm ? 'Try a different search' : 'Start a new chat to get in touch with your team'}
              </p>
              {!searchTerm && (
                <Button
                  className="mt-4 btn-volt"
                  onClick={() => setShowNewRoomSheet(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Chat
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filteredRooms.map((room) => {
              const { title, site } = parseRoomName(room.name);
              const hasUnread = room.unread_count > 0;
              
              return (
                <button
                  key={room.id}
                  onClick={() => {
                    openRoom(room);
                    navigate(`/tenant-mobile/chat/${room.id}`);
                  }}
                  className={`w-full p-4 rounded-lg text-left transition-colors ${
                    hasUnread ? 'bg-[#3a3a3a] border border-[#ffd108]/30' : 'bg-[#2a2a2a] hover:bg-[#3a3a3a]'
                  }`}
                  data-testid={`chat-room-${room.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="relative">
                      <div className={`p-2 rounded-full ${
                        room.room_type === 'job' ? 'bg-blue-500/20' : 'bg-[#ffd108]/20'
                      }`}>
                        {room.room_type === 'job' ? (
                          <Briefcase className="h-5 w-5 text-blue-400" />
                        ) : (
                          <Users className="h-5 w-5 text-[#ffd108]" />
                        )}
                      </div>
                      {hasUnread && (
                        <span className="absolute -top-1 -right-1 bg-[#ffd108] text-black text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                          {room.unread_count > 9 ? '9+' : room.unread_count}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium truncate text-white">{title}</h3>
                        {room.last_message_at && (
                          <span className="text-xs text-gray-500 ml-2 shrink-0">
                            {formatDistanceToNow(new Date(room.last_message_at), { addSuffix: true })}
                          </span>
                        )}
                      </div>
                      {site && (
                        <p className="text-xs text-gray-400 truncate">{site}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <Badge 
                          variant="secondary" 
                          className={`text-xs ${
                            room.room_type === 'job' 
                              ? 'bg-blue-500/20 text-blue-400' 
                              : 'bg-gray-700 text-gray-300'
                          }`}
                        >
                          {room.room_type === 'job' ? 'Job Chat' : 'General'}
                        </Badge>
                        {hasUnread && (
                          <span className="text-xs text-[#ffd108] font-medium">New</span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* New Chat Room Sheet */}
      <Sheet open={showNewRoomSheet} onOpenChange={setShowNewRoomSheet}>
        <SheetContent side="bottom" className="bg-[#2a2a2a] border-gray-700 h-[80vh]">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-[#ffd108]" />
              New Chat Room
            </SheetTitle>
          </SheetHeader>
          
          <div className="space-y-4 py-4 overflow-y-auto">
            <div className="space-y-2">
              <label className="text-gray-400 text-xs uppercase">Room Name *</label>
              <Input
                value={newRoomName}
                onChange={(e) => setNewRoomName(e.target.value)}
                placeholder="e.g., Team Chat, Site Updates"
                className="bg-[#1a1a1a] border-gray-700 text-white"
                data-testid="new-room-name"
              />
            </div>

            <div className="space-y-2">
              <label className="text-gray-400 text-xs uppercase">Room Type</label>
              <Select value={newRoomType} onValueChange={setNewRoomType}>
                <SelectTrigger className="bg-[#1a1a1a] border-gray-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2a2a2a] border-gray-700">
                  <SelectItem value="general">General Chat</SelectItem>
                  <SelectItem value="announcement">Announcement</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-gray-400 text-xs uppercase">
                Add Operators ({selectedOperators.length} selected)
              </label>
              <div className="max-h-48 overflow-y-auto space-y-2 p-2 bg-[#1a1a1a] rounded-lg">
                {operators.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">No operators available</p>
                ) : (
                  operators.map(op => (
                    <button
                      key={op.id}
                      type="button"
                      onClick={() => toggleOperator(op.id)}
                      className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${
                        selectedOperators.includes(op.id)
                          ? 'bg-[#ffd108]/20 border border-[#ffd108]/50'
                          : 'bg-[#2a2a2a] hover:bg-[#3a3a3a]'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        selectedOperators.includes(op.id) ? 'bg-[#ffd108]' : 'bg-gray-700'
                      }`}>
                        <span className={`text-sm font-medium ${
                          selectedOperators.includes(op.id) ? 'text-black' : 'text-white'
                        }`}>
                          {op.name?.charAt(0) || '?'}
                        </span>
                      </div>
                      <span className="text-white text-sm">{op.name}</span>
                      {selectedOperators.includes(op.id) && (
                        <span className="ml-auto text-[#ffd108] text-xs">Selected</span>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>

          <SheetFooter className="flex gap-2 pt-4">
            <Button
              variant="outline"
              className="flex-1 border-gray-700"
              onClick={() => setShowNewRoomSheet(false)}
              disabled={creatingRoom}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 btn-volt"
              onClick={handleCreateRoom}
              disabled={creatingRoom || !newRoomName.trim()}
              data-testid="create-room-btn"
            >
              {creatingRoom ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Create Room
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </TenantMobileLayout>
  );
};

export default TenantMobileChat;
